import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api, AuthUser } from './useAuth';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  branchIds: number[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: string;
  branchIds?: number[];
}

export interface UpdateUserRequest {
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  branchIds?: number[];
}

function mapUser(raw: Record<string, unknown>): User {
  const branchIds = (raw.branchIds ?? raw.branch_ids ?? []) as number[];
  const created = raw.createdAt ?? raw.created_at;
  const updated = raw.updatedAt ?? raw.updated_at;
  return {
    id: String(raw.id ?? ''),
    email: String(raw.email ?? ''),
    firstName: String(raw.firstName ?? raw.first_name ?? ''),
    lastName: String(raw.lastName ?? raw.last_name ?? ''),
    role: String(raw.role ?? ''),
    branchIds: Array.isArray(branchIds) ? branchIds.map(Number) : [],
    createdAt: created ? String(created) : '',
    updatedAt: updated ? String(updated) : '',
  };
}

export interface ResetPasswordRequest {
  newPassword: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface UsersResponse {
  users: User[];
}

// Fetch users with search and filter
export const useUsers = (search?: string, role?: string) => {
  return useQuery<UsersResponse>(
    ['users', search, role],
    async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (role) params.append('role', role);
      
      const response = await api.get(`/users?${params.toString()}`);
      const users = (response.data?.users ?? []).map((u: Record<string, unknown>) => mapUser(u));
      return { users };
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

// Create user mutation
export const useCreateUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation<User, Error, CreateUserRequest>(
    async (userData) => {
      const response = await api.post('/users', userData);
      return response.data.user;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users']);
      },
    }
  );
};

// Update user mutation
export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation<User, Error, { id: string; data: UpdateUserRequest }>(
    async ({ id, data }) => {
      const response = await api.put(`/users/${id}`, data);
      return response.data.user;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users']);
      },
    }
  );
};

// Reset password mutation
export const useResetPassword = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, { id: string; data: ResetPasswordRequest }>(
    async ({ id, data }) => {
      await api.post(`/users/${id}/reset-password`, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users']);
      },
    }
  );
};

// Delete user mutation
export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, string>(
    async (id) => {
      await api.delete(`/users/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users']);
      },
    }
  );
};

export interface ChangePasswordResponse {
  message: string;
  token: string;
  user: AuthUser;
}

// Change password mutation (for current user)
export const useChangePassword = () => {
  return useMutation<ChangePasswordResponse, Error, ChangePasswordRequest>(
    async (data) => {
      const response = await api.post<ChangePasswordResponse>('/users/reset-password', data);
      return response.data;
    }
  );
};
